// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Trash2, Plus, Search, Edit3 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface EditorUser {
  id: string;
  user_id: string;
  name: string;
  email: string;
  status: string;
  created_at: string;
}

interface NewEditor {
  name: string;
  email: string;
  password: string;
}

const EditorsManagement: React.FC = () => {
  const [editors, setEditors] = useState<EditorUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newEditor, setNewEditor] = useState<NewEditor>({
    name: '',
    email: '',
    password: ''
  });

  const fetchEditors = async () => {
    try {
      setIsLoading(true);
      
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('id, user_id, name, email, status, created_at')
        .eq('role', 'editor')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEditors(profiles || []);
    } catch (error) {
      console.error('Error fetching editors:', error);
      toast.error('Failed to load editors');
    } finally {
      setIsLoading(false);
    }
  };

  const createEditor = async () => {
    try {
      setIsCreating(true);

      // Create auth user via edge function
      const { data, error } = await invokeEdgeFunction('admin-auth-management', {
        body: { 
          action: 'create_user',
          email: newEditor.email,
          password: newEditor.password,
          userData: {
            name: newEditor.name,
            role: 'editor'
          }
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success('Editor created successfully');
      setNewEditor({ name: '', email: '', password: '' });
      setShowCreateForm(false);
      fetchEditors();
    } catch (error) {
      console.error('Error creating editor:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create editor');
    } finally {
      setIsCreating(false);
    }
  };

  const deleteEditor = async (editorId: string, editorName: string) => {
    if (!confirm(`Are you sure you want to delete editor "${editorName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', editorId);

      if (error) throw error;
      
      toast.success('Editor deleted successfully');
      fetchEditors();
    } catch (error) {
      console.error('Error deleting editor:', error);
      toast.error('Failed to delete editor');
    }
  };

  useEffect(() => {
    fetchEditors();
  }, []);

  const filteredEditors = editors.filter(editor =>
    editor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    editor.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary-700">
            Editor Management
          </h1>
          <p className="text-gray-600">
            Manage editors who can access camp and NCAA calendar management
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Editor
        </Button>
      </div>

      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Editor</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={newEditor.name}
                  onChange={(e) => setNewEditor({...newEditor, name: e.target.value})}
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newEditor.email}
                  onChange={(e) => setNewEditor({...newEditor, email: e.target.value})}
                  placeholder="Enter email address"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password">Temporary Password</Label>
              <Input
                id="password"
                type="password"
                value={newEditor.password}
                onChange={(e) => setNewEditor({...newEditor, password: e.target.value})}
                placeholder="Enter temporary password"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={createEditor}
                disabled={isCreating || !newEditor.name || !newEditor.email || !newEditor.password}
              >
                {isCreating ? 'Creating...' : 'Create Editor'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowCreateForm(false)}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Editors ({filteredEditors.length})</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search editors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading editors...</div>
          ) : filteredEditors.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? 'No editors found matching your search' : 'No editors found'}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredEditors.map((editor) => (
                <div
                  key={editor.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <Edit3 className="h-4 w-4 text-primary-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">{editor.name}</h4>
                      <p className="text-sm text-gray-600">{editor.email}</p>
                      <p className="text-xs text-gray-500">
                        Created {new Date(editor.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={editor.status === 'active' ? 'default' : 'secondary'}>
                      {editor.status}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteEditor(editor.id, editor.name)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EditorsManagement;